/**
 * CPA-ONBOARDING-1 — address, logo, and disclaimer boilerplate.
 *
 * The logo control posts to POST /v1/documents/upload-logo — the SAME endpoint
 * BrandRegistrationPage uses. One logo path, not two; this page only stores the
 * blob URL the upload returns.
 */
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import type { FormState, UpdateField } from './model'

interface Props {
  form: FormState
  update: UpdateField
  licenseStates: string[]
  uploading: boolean
  onLogoUpload: (file: File) => void
}

export function FirmDetailsCards({ form, update, licenseStates, uploading, onLogoUpload }: Props) {
  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Address &amp; Logo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="firm_address_street">Street</Label>
            <Input
              id="firm_address_street"
              value={form.firm_address_street}
              onChange={e => update('firm_address_street', e.target.value)}
              placeholder="1200 Main St, Suite 400"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="firm_address_city">City</Label>
              <Input
                id="firm_address_city"
                value={form.firm_address_city}
                onChange={e => update('firm_address_city', e.target.value)}
                placeholder="Dallas"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="firm_address_state">State</Label>
              <Select
                value={form.firm_address_state || undefined}
                onValueChange={val => update('firm_address_state', val ?? '')}
              >
                <SelectTrigger id="firm_address_state">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {licenseStates.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="firm_address_zip">ZIP</Label>
              <Input
                id="firm_address_zip"
                value={form.firm_address_zip}
                onChange={e => update('firm_address_zip', e.target.value)}
                placeholder="75201"
                maxLength={10}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="firm_logo">Logo</Label>
            <Input
              id="firm_logo"
              type="file"
              accept="image/*"
              onChange={e => { const f = e.target.files?.[0]; if (f) onLogoUpload(f) }}
              disabled={uploading}
            />
            {uploading && <p className="text-xs text-muted-foreground">Uploading…</p>}
            {form.firm_logo_ref && (
              <div className="flex items-center gap-3 pt-1">
                <img
                  src={form.firm_logo_ref}
                  alt={form.firm_legal_name || 'Firm logo'}
                  className="h-10 max-w-[160px] object-contain"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => update('firm_logo_ref', '')}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Disclaimer Boilerplate</CardTitle>
          <CardDescription>
            Appended to client-facing deliverables. Free text — your firm's own wording.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            id="disclaimer_boilerplate"
            value={form.disclaimer_boilerplate}
            onChange={e => update('disclaimer_boilerplate', e.target.value)}
            rows={5}
            placeholder="This report was prepared solely for the use of…"
          />
        </CardContent>
      </Card>
    </div>
  )
}
